class PersonalisationDrawer extends HTMLElement {
  constructor() {
    super();

    this.personalisationConfirm = document.querySelector('personalisation-confirm');
    this.confirmButton = this.querySelector('#PersonalisationConfirm');
    this.clickedPersonalisationButton = null;
    this.cartItemContext = null;
    this.bsbCallback = null;
    this.setEventListeners();


    window.addEventListener('personalisation-bsb:open', this.handleBsbOpen.bind(this));
  }

  setEventListeners() {
    const initialsInput = this.querySelector('#PersonalisationInitials');
    const termsInput = this.querySelector('#AcceptsPersonalisationTerms');
    const fontRadioGroup = this.querySelector('.personalisation-drawer__fonts');
    const colorRadioGroup = this.querySelector('.personalisation-drawer__thread-colors');
    const overlay = this.querySelector('#PersonalisationDrawer-Overlay');

    // Add escape key listener
    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.closeDrawer());
    
    // Add overlay click listener
    if (overlay) {
      overlay.addEventListener('click', this.closeDrawer.bind(this));
    }

    // Use event delegation to handle clicks on any PersonalisationDrawerOpener button
    // This works for buttons on the product page and in the cart drawer
    document.addEventListener('click', (event) => {
      const personalisationButton = event.target.closest('#PersonalisationDrawerOpener') || 
                                    event.target.closest('.personalisation-drawer__opener') ||
                                    event.target.closest('.cart-item__personalisation-edit-button-option');
      if (personalisationButton && !personalisationButton.disabled) {
        event.preventDefault();
        event.stopPropagation();
        this.clickedPersonalisationButton = personalisationButton;
        
        // Check if button is in cart drawer context
        // Button can be directly in tr.cart-item OR in tr.cart-item__addons-row (sibling to cart-item)
        let cartItemRow = personalisationButton.closest('tr.cart-item') || 
                          personalisationButton.closest('.cart-item');
        
        // If not found directly, check if button is in addons row (sibling to cart-item row)
        if (!cartItemRow) {
          const addonsRow = personalisationButton.closest('tr.cart-item__addons-row');
          if (addonsRow) {
            // Loop through previous siblings to find the cart-item row (skip other addons rows)
            let prevSibling = addonsRow.previousElementSibling;
            while (prevSibling && !prevSibling.classList.contains('cart-item')) {
              prevSibling = prevSibling.previousElementSibling;
            }
            cartItemRow = prevSibling;
          }
        }
        
        if (cartItemRow) {
          const itemKeyElement = cartItemRow.querySelector('[data-itemkeyid]');
          const itemKey = itemKeyElement?.getAttribute('data-itemkeyid');
          
          const rowIndex = cartItemRow.getAttribute('data-index');
          const rowId = cartItemRow.getAttribute('id');
          const itemLineOnCart = cartItemRow.querySelector('[data-itemlineoncart]')?.getAttribute('data-itemlineoncart');
          const variantId = personalisationButton.getAttribute('data-variant-id') || 
                           personalisationButton.dataset.variantId;
          
          let lineNumber = null;
          if (itemLineOnCart) {
            lineNumber = parseInt(itemLineOnCart);
          } else if (rowIndex) {
            lineNumber = parseInt(rowIndex);
          } else if (rowId && rowId.includes('CartDrawer-Item-')) {
            lineNumber = parseInt(rowId.replace('CartDrawer-Item-', ''));
          }
          
          this.cartItemContext = {
            line: lineNumber,
            itemKey: itemKey,
            variantId: variantId
          };
          
          GiftUtils.log('Cart item context stored:', this.cartItemContext);
        } else {
          this.cartItemContext = null;
        }
        
        this.openDrawer();
    
      }
    });

    // Add initials input listener
    if (initialsInput) {
      initialsInput.addEventListener('input', (event) => {
        this.checkInitialsLength(event.target);
      });
    }

    // Add confirm button listener
    if (this.confirmButton) {
      this.confirmButton.addEventListener('click', () => {
        if (this.bsbCallback) {
          this.addPersonalisationForBsb();
        } else if (this.cartItemContext) {
          this.addPersonalisationToCartItem();
        } else {
          this.addPersonalisationToProduct();
          this.updateAddToCartButton();
        }
      });
    }

    // Add terms checkbox listener
    if (termsInput) {
      termsInput.addEventListener('click', (event) => {
        if (event.target.checked) {
          this.querySelector('#AcceptsPersonalisationTerms').classList.remove('error');
          const termsNote = this.querySelector('.personalisation-drawer__note--terms');
          if (termsNote) termsNote.classList.remove('error');
        }
      });
    }

    // Add font radio group listener
    if (fontRadioGroup) {
      fontRadioGroup.addEventListener('change', () => {
        const radioValue = this.getRadioGroupValue(this.querySelectorAll('.personalisation-drawer__font-radio'));

        if (radioValue) {
          const fontErrorWrapper = this.querySelector('.personalisation-drawer__note--font');

          if (fontErrorWrapper) {
            fontErrorWrapper.classList.remove('error');
            fontErrorWrapper.innerHTML = radioValue;
          }
        }
      });
    }

    // Add color radio group listener
    if (colorRadioGroup) {
      colorRadioGroup.addEventListener('change', () => {
        const radioValue = this.getRadioGroupValue(this.querySelectorAll('.personalisation-drawer__color-radio'));

        if (radioValue) {
          const colorErrorWrapper = this.querySelector('.personalisation-drawer__note--color');

          if (colorErrorWrapper) {
            colorErrorWrapper.classList.remove('error');
            colorErrorWrapper.innerHTML = radioValue;
          }
        }
      });
    }
  }

  openDrawer() {
    this.classList.add('animate', 'active');

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('PersonalisationDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    GiftUtils.setBodyOverflowHidden(true);
  }

  closeDrawer() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    GiftUtils.setBodyOverflowHidden(false);
  }

  checkInitialsLength(element) {

    // Get dynamic character limit from data attribute or fallback to 3
    const initialsMax = parseInt(element.getAttribute('data-max-length')) || 3;
    const format = /^[A-Za-z]+$/;
    const hasOnlyLetters = format.test(element.value);

    if (!hasOnlyLetters) {
      element.value = element.value.replace(/[^\w\s]/gi, '').replace(/[0-9_]/gi, '');
    }

    const initialsLength = element.value.length;

    if (initialsLength > 0) {
      const initialsNote = this.querySelector('.personalisation-drawer__note--initials');
      if (initialsNote) {
        initialsNote.innerHTML = '';
      }
    }

    if (initialsLength > initialsMax) {
      element.value = element.value.substring(0, initialsMax);
    } else {
      element.value = element.value.toUpperCase();
      const initialsCount = this.querySelector('.personalisation-drawer__initials-count');
      if (initialsCount) {
        initialsCount.innerHTML = initialsLength + '/' + initialsMax;
      }
    }
  }

  handleBsbOpen(event) {
    const { callback, existing } = event.detail || {};
    this.bsbCallback = callback ?? null;
    this.cartItemContext = null;
    this.clickedPersonalisationButton = null;
    this.resetDrawerForm();
    if (existing) {
      this.prefillDrawerForm(existing);
    }

    this.openDrawer();
  }

  resetDrawerForm() {
    const initialsInput = this.querySelector('#PersonalisationInitials');
    const termsInput = this.querySelector('#AcceptsPersonalisationTerms');
    if (initialsInput) initialsInput.value = '';
    if (termsInput) termsInput.checked = false;

    const checkedFont = this.querySelector('input[name="personalisation-font"]:checked');
    if (checkedFont) checkedFont.checked = false;
    const checkedColor = this.querySelector('input[name="personalisation-color"]:checked');
    if (checkedColor) checkedColor.checked = false;

    const initialsCount = this.querySelector('.personalisation-drawer__initials-count');
    if (initialsCount) {
      const max = initialsInput ? (parseInt(initialsInput.getAttribute('data-max-length')) || 3) : 3;
      initialsCount.innerHTML = `0/${max}`;
    }
    const fontNote = this.querySelector('.personalisation-drawer__note--font');
    const colorNote = this.querySelector('.personalisation-drawer__note--color');
    const initialsNote = this.querySelector('.personalisation-drawer__note--initials');
    if (fontNote) fontNote.innerHTML = '';
    if (colorNote) colorNote.innerHTML = '';
    if (initialsNote) initialsNote.innerHTML = '';
  }

  prefillDrawerForm(existing) {
    const initialsInput = this.querySelector('#PersonalisationInitials');
    if (initialsInput && existing.initials) {
      initialsInput.value = existing.initials;
      this.checkInitialsLength(initialsInput);
    }
    if (existing.font) {
      const fontRadio = this.querySelector(`input[name="personalisation-font"][value="${existing.font}"]`);
      if (fontRadio) {
        fontRadio.checked = true;
        const fontNote = this.querySelector('.personalisation-drawer__note--font');
        if (fontNote) fontNote.innerHTML = existing.font;
      }
    }
    if (existing.color) {
      const colorRadio = this.querySelector(`input[name="personalisation-color"][value="${existing.color}"]`);
      if (colorRadio) {
        colorRadio.checked = true;
        const colorNote = this.querySelector('.personalisation-drawer__note--color');
        if (colorNote) colorNote.innerHTML = existing.color;
      }
    }
  }

  addPersonalisationForBsb() {
    const personalisedData = {
      initials: this.querySelector('#PersonalisationInitials').value,
      font: this.getRadioGroupValue(this.querySelectorAll('.personalisation-drawer__font-radio')),
      color: this.getRadioGroupValue(this.querySelectorAll('.personalisation-drawer__color-radio')),
      termsChecked: this.isTermsChecked(),
    };

    this.errorHandling(personalisedData);

    const isSomeDataMissing = Object.keys(personalisedData).some(
      (key) => personalisedData[key] === '' || personalisedData[key] === false
    );

    if (!isSomeDataMissing) {
      const callback = this.bsbCallback;
      this.bsbCallback = null;
      this.closeDrawer();
      callback({
        initials: personalisedData.initials,
        font: personalisedData.font,
        color: personalisedData.color,
      });
    }
  }

  addPersonalisationToProduct() {
    const personalisedData = {
      initials: this.querySelector('#PersonalisationInitials').value,
      font: this.getRadioGroupValue(this.querySelectorAll('.personalisation-drawer__font-radio')),
      color: this.getRadioGroupValue(this.querySelectorAll('.personalisation-drawer__color-radio')),
      termsChecked: this.isTermsChecked(),
      variantId: this.clickedPersonalisationButton?.dataset.variantId || 
                 document.querySelector('#PersonalisationDrawerOpener')?.dataset.variantId,
    };

    this.errorHandling(personalisedData);

    const isSomeDataMissing = Object.keys(personalisedData).some(
      (key) => personalisedData[key] === '' || personalisedData[key] === false
    );

    if (!isSomeDataMissing) {
      this.closeDrawer();
      this.personalisationConfirm.handlePersonalisedDataUpdate(personalisedData);
      this.personalisationConfirm.enablePersonalisationPropertyInputs();
      this.clickedPersonalisationButton = null;
      this.cartItemContext = null;
    }
  }

  async addPersonalisationToCartItem() {
    const personalisedData = {
      initials: this.querySelector('#PersonalisationInitials').value,
      font: this.getRadioGroupValue(this.querySelectorAll('.personalisation-drawer__font-radio')),
      color: this.getRadioGroupValue(this.querySelectorAll('.personalisation-drawer__color-radio')),
      termsChecked: this.isTermsChecked(),
    };

    this.errorHandling(personalisedData);

    const isSomeDataMissing = Object.keys(personalisedData).some(
      (key) => personalisedData[key] === '' || personalisedData[key] === false
    );

    if (!isSomeDataMissing) {
      this.closeDrawer();
      await this.updateCartItemWithPersonalisation(this.cartItemContext, personalisedData);
      this.clickedPersonalisationButton = null;
      this.cartItemContext = null;
    }
  }

  async updateCartItemWithPersonalisation(cartItemContext, personalisedData) {
    // Get the cart item row element for UI updates
    const cartItemRow = this.clickedPersonalisationButton?.closest('tr.cart-item') || 
                       this.clickedPersonalisationButton?.closest('.cart-item') ||
                       document.querySelector(`#CartDrawer-Item-${cartItemContext.line}`) ||
                       document.querySelector(`.cart-item[data-itemkeyid="${cartItemContext.itemKey}"]`)
    
    const addPersonalisationCell = cartItemRow?.querySelector('.cart-item__add-personalisation')
    const loadingSpinner = cartItemRow?.querySelector('.cart-item__add-personalisation .loading__spinner')
    const personalisationButton = cartItemRow?.querySelector('.cart-item__add-personalisation .personalisation-button')
    try {
      // Show loading state on cart drawer line items
      GiftUtils.setCartLoading(true)

      // Show loading state and hide button
      if (cartItemRow) {
        if (loadingSpinner) {
          loadingSpinner.classList.remove('hidden')
        }
        if (personalisationButton) {
          personalisationButton.style.display = 'none'
        }
      }
      
      // Get the current cart to find the line item
      const cart = await GiftUtils.fetchCart();

      // Find the cart item by key (most reliable) or line number
      let cartItem = null;
      let lineNumber = null;
      let itemIndex = -1;

      // First try to find by item key (most reliable)
      if (cartItemContext.itemKey) {
        itemIndex = cart.items.findIndex(item => item.key === cartItemContext.itemKey);
        if (itemIndex !== -1) {
          cartItem = cart.items[itemIndex];
          lineNumber = itemIndex + 1;
        }
      }
      
      // If not found by key, try by line number
      if (!cartItem && cartItemContext.line) {
        lineNumber = parseInt(cartItemContext.line);
        if (lineNumber > 0 && lineNumber <= cart.items.length) {
          itemIndex = lineNumber - 1;
          cartItem = cart.items[itemIndex];
          lineNumber = itemIndex + 1;
        }
      }

      if (!cartItem) {
        GiftUtils.log('Could not find cart item to update', {
          cartItemContext,
          cartItems: cart.items.map((it, idx) => ({ 
            variant_id: it.variant_id,
            key: it.key, 
            index: idx,
            calculatedLine: idx + 1
          }))
        });
        return;
      }

      // Ensure we have a valid line number
      if (!lineNumber || isNaN(lineNumber)) {
        if (itemIndex !== -1) {
          lineNumber = itemIndex + 1;
        } else {
          itemIndex = cart.items.indexOf(cartItem);
          if (itemIndex !== -1) {
            lineNumber = itemIndex + 1;
          }
        }
      }

      if (!lineNumber || isNaN(lineNumber)) {
        GiftUtils.log('No valid line number found for cart item');
        return;
      }

      lineNumber = parseInt(lineNumber);

      // Property names come from the locale via window.personalisationStrings
      const initialsProperty = window.personalisationStrings?.initialsProperty;
      const fontProperty = window.personalisationStrings?.fontProperty;
      const colorProperty = window.personalisationStrings?.colorProperty;

      if (!initialsProperty || !fontProperty || !colorProperty) {
        GiftUtils.log('Missing personalisationStrings — cannot update cart item', {
          initialsProperty,
          fontProperty,
          colorProperty
        });
        return;
      }

      // Get existing properties
      const existingProperties = { ...(cartItem.properties || {}) };
      
      // Generate or use existing groupId
      const groupId = existingProperties._groupId || GiftUtils.generateGroupId();
      
      // Update properties with personalisation data
      existingProperties[initialsProperty] = personalisedData.initials;
      existingProperties[fontProperty] = personalisedData.font;
      existingProperties[colorProperty] = personalisedData.color;
      existingProperties._groupId = groupId;

      // Update main cart item with personalisation properties
      GiftUtils.log('Updating cart item with personalisation:', {
        cartItem: { line: lineNumber, key: cartItem.key, variant_id: cartItem.variant_id, quantity: cartItem.quantity }
      });

      if (cartItem.quantity > 1) {
        // Split: reduce existing line by 1 (keep original properties unchanged)
        const reducePayload = {
          line: lineNumber,
          quantity: cartItem.quantity - 1,
        };

        GiftUtils.log('Reducing existing line quantity:', reducePayload);
        const reduceResponse = await GiftUtils.cartPost('/cart/change.js', reducePayload);

        if (!reduceResponse.ok) {
          const errorText = await reduceResponse.text();
          console.error('[Personalisation Debug] Failed to reduce cart item quantity:', reduceResponse.status, reduceResponse.statusText, errorText);
          return;
        }

        // Add new line with qty 1 + personalisation properties
        const addPayload = {
          id: cartItem.variant_id,
          quantity: 1,
          properties: existingProperties,
        };
        const sellingPlanId = cartItem.selling_plan_allocation?.selling_plan?.id;
        if (sellingPlanId) addPayload.selling_plan = sellingPlanId;

        GiftUtils.log('Adding split personalised line:', addPayload);
        const addResponse = await GiftUtils.cartPost('/cart/add.js', addPayload);

        if (!addResponse.ok) {
          const errorText = await addResponse.text();
          console.error('[Personalisation Debug] Failed to add personalised line:', addResponse.status, addResponse.statusText, errorText);
          return;
        }
      } else {
        // qty == 1: update in-place (existing behavior)
        const updatePayload = {
          line: lineNumber,
          quantity: 1,
          properties: existingProperties,
        };

        const updateResponse = await GiftUtils.cartPost('/cart/change.js', updatePayload);

        if (!updateResponse.ok) {
          const errorText = await updateResponse.text();
          console.error('[Personalisation Debug] Failed to update cart item:', updateResponse.status, updateResponse.statusText, errorText);
          return;
        }
      }

      // Check if personalisation addon already exists for this groupId
      const refreshedCart = await GiftUtils.fetchCart();
      const existingPersonalisation = GiftUtils.findAddon(refreshedCart.items, groupId, null, 'Personalisation');

      if (!existingPersonalisation) {
        // Add personalisation addon item
        const personalisationProductId = this.dataset.monogramProduct;
        if (!personalisationProductId) {
          GiftUtils.log('No monogram product ID found');
          return;
        }

        const addPersonalisationPayload = {
          id: parseInt(personalisationProductId),
          quantity: 1,
          properties: {
            _groupId: groupId,
            _addon: 'true',
            _addonType: 'Personalisation',
          },
        };

        GiftUtils.log('Adding personalisation addon:', addPersonalisationPayload);

        const addResponse = await GiftUtils.cartPost('/cart/add.js', addPersonalisationPayload);

        if (!addResponse.ok) {
          const errorText = await addResponse.text();
          console.error('[Personalisation Debug] Failed to add personalisation addon:', addResponse.status, addResponse.statusText, errorText);
          return;
        }
      }

      // Trigger cart update to refresh the UI
      GiftUtils.refreshCartUI();
      if (window.updateCartItemCount) {
        await window.updateCartItemCount();
      }
    } catch (error) {
      console.error('[Personalisation Debug] Error updating cart item with personalisation:', error);
    } finally {
      // Remove cart drawer loading state
      GiftUtils.setCartLoading(false)

      // Hide loading spinner
      if (cartItemRow && loadingSpinner) {
        loadingSpinner.classList.add('hidden')
      }
      // Note: personalisation button visibility will be handled by server rendering after cart update
    }
  }

  removePersonalization() {
    // Empty product properties
    const productInitials = document.querySelector('#ProductPropertiesInitials');
    const productFont = document.querySelector('#ProductPropertiesFont');
    const productColor = document.querySelector('#ProductPropertiesColor');
    
    if (productInitials) productInitials.value = '';
    if (productFont) productFont.value = '';
    if (productColor) productColor.value = '';

    // Clear personalisation data in confirm box
    const confirmInitials = document.querySelector('#ProductConfirmInitials');
    const confirmFont = document.querySelector('#ProductConfirmFont');
    const confirmColor = document.querySelector('#ProductConfirmColor');
    
    if (confirmInitials) confirmInitials.innerHTML = '';
    if (confirmFont) confirmFont.innerHTML = '';
    if (confirmColor) confirmColor.innerHTML = '';

    // Set Personalisation Drawer inputs to default
    const drawerInitials = this.querySelector('#PersonalisationInitials');
    const drawerTerms = this.querySelector('#AcceptsPersonalisationTerms');
    
    if (drawerInitials) drawerInitials.value = '';
    if (drawerTerms) drawerTerms.checked = false;
    
    // Clear selected radio buttons safely
    const selectedFont = this.querySelector('input[name="personalisation-font"]:checked');
    if (selectedFont) {
      selectedFont.checked = false;
    }
    
    const selectedColor = this.querySelector('input[name="personalisation-color"]:checked');
    if (selectedColor) {
      selectedColor.checked = false;
    }

    // Remove selected data info from font and color heading
    const fontNote = this.querySelector('.personalisation-drawer__note--font');
    const colorNote = this.querySelector('.personalisation-drawer__note--color');
    const initialsCount = this.querySelector('.personalisation-drawer__initials-count');
    
    if (fontNote) fontNote.innerHTML = '';
    if (colorNote) colorNote.innerHTML = '';
    
    // Reset initials count with dynamic character limit
    if (initialsCount) {
      const initialsInput = this.querySelector('#PersonalisationInitials');
      const maxLength = initialsInput ? parseInt(initialsInput.getAttribute('data-max-length')) || 3 : 3;
      initialsCount.innerHTML = '0/' + maxLength;
    }

    if (this.personalisationConfirm) {
      this.personalisationConfirm.disablePersonalisationPropertyInputs();
    }
  }

  errorHandling(personalisedData) {
    if (!personalisedData.initials) {
      const initialsError = this.querySelector('.personalisation-drawer__note--initials');
      if (initialsError) {
        initialsError.innerHTML = window.personalisationStrings.initialsError;
      }
    }

    if (!personalisedData.font) {
      const fontErrorWrapper = this.querySelector('.personalisation-drawer__note--font');

      if (fontErrorWrapper) {
        fontErrorWrapper.classList.add('error');
        fontErrorWrapper.innerHTML = window.personalisationStrings.fontError;
      }
    }

    if (!personalisedData.color) {
      const colorErrorWrapper = this.querySelector('.personalisation-drawer__note--color');

      if (colorErrorWrapper) {
        colorErrorWrapper.classList.add('error');
        colorErrorWrapper.innerHTML = window.personalisationStrings.colorError;
      }
    }

    if (!personalisedData.termsChecked) {
      const termsElement = this.querySelector('#AcceptsPersonalisationTerms');
      if (termsElement) {
        termsElement.classList.add('error');
      }
      const termsNote = this.querySelector('.personalisation-drawer__note--terms');
      if (termsNote) termsNote.classList.add('error');
    }
  }

  updateAddToCartButton() {
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return;

    const section = variantSelects.dataset.originalSection || variantSelects.dataset.section;
    const addToCartButton = document.querySelector(`#ProductSubmitButton-${section}`);
    const isAddToCartDisabled = addToCartButton?.hasAttribute('disabled') ?? false;

    variantSelects.toggleAddButton(isAddToCartDisabled, '');
  }

  isTermsChecked() {
    const termsInput = this.querySelector('#AcceptsPersonalisationTerms');
    return termsInput.checked;
  }


  getRadioGroupValue(radioGroup) {
    let radioValue = '';

    radioGroup.forEach((radio) => {
      if (radio.checked) {
        radioValue = radio.value;
      }
    });

    return radioValue;
  }
}

// Only define the custom element if it hasn't been defined already
if (!customElements.get('personalisation-drawer')) {
  customElements.define('personalisation-drawer', PersonalisationDrawer);
}

class PersonalisationConfirm extends HTMLElement {
  constructor() {
    super();

    this.personalisationDrawer = document.querySelector('personalisation-drawer');
    this.personalisationConfirmBox = this.querySelector('#PersonalisationConfirmBox');
    this.personalisationButton = document.querySelector('#PersonalisationDrawerOpener');
    this.setEventListeners();
  }

  setEventListeners() {
    const confirmEditButton = this.querySelector('#PersonalisationConfirmEdit');
    const confirmRemoveButton = this.querySelector('#PersonalisationConfirmRemove');
    
    if (confirmEditButton) {
      confirmEditButton.addEventListener('click', () => {
        if (this.personalisationDrawer) {
          this.personalisationDrawer.openDrawer(this.personalisationButton);
        }
      });
    }

    if (confirmRemoveButton) {
      confirmRemoveButton.addEventListener('click', () => {
        if (this.personalisationButton) {
          this.personalisationButton.style.display = 'block';
        }
        if (this.personalisationConfirmBox) {
          this.personalisationConfirmBox.style.display = 'none';
        }
        if (this.personalisationDrawer) {
          this.personalisationDrawer.removePersonalization();
          this.personalisationDrawer.updateAddToCartButton();
        }
      });
    }
  }

  updateProductData(personalisedData) {
    const initialsInput = this.querySelector('#ProductPropertiesInitials');
    const fontInput = this.querySelector('#ProductPropertiesFont');
    const colorInput = this.querySelector('#ProductPropertiesColor');
    
    if (initialsInput) initialsInput.value = personalisedData.initials;
    if (fontInput) fontInput.value = personalisedData.font;
    if (colorInput) colorInput.value = personalisedData.color;
  }

  updateConfirmBox(personalisedData) {
    const confirmInitials = this.querySelector('#ProductConfirmInitials');
    const confirmFont = this.querySelector('#ProductConfirmFont');
    const confirmColor = this.querySelector('#ProductConfirmColor');
    
    if (confirmInitials) confirmInitials.innerHTML = personalisedData.initials;
    if (confirmFont) confirmFont.innerHTML = personalisedData.font;
    if (confirmColor) confirmColor.innerHTML = personalisedData.color;
    
    if (this.personalisationButton) {
      this.personalisationButton.style.display = 'none';
    }
    if (this.personalisationConfirmBox) {
      this.personalisationConfirmBox.style.display = 'block';
    }
  }

  handlePersonalisedDataUpdate(personalisedData) {
    this.updateProductData(personalisedData);
    this.updateConfirmBox(personalisedData);
  }

  enablePersonalisationPropertyInputs() {
    const properties = this.querySelectorAll('.personalisation-property');

    properties.forEach((property) => {
      property.removeAttribute('disabled');
    });
  }

  disablePersonalisationPropertyInputs() {
    const properties = this.querySelectorAll('.personalisation-property');

    properties.forEach((property) => {
      property.setAttribute('disabled', 'disabled');
    });
  }
}

// Only define the custom element if it hasn't been defined already
if (!customElements.get('personalisation-confirm')) {
  customElements.define('personalisation-confirm', PersonalisationConfirm);
}
